import React, { useState, useEffect, useRef } from "react";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import "./Careers.css";
import {
  FaMapMarkerAlt,
  FaGlobe,
  FaChartLine,
  FaHandshake,
  FaLightbulb,
  FaChevronDown,
} from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_CAREERS_API_BASE_URL;

const EMPTY_FORM = { name: "", email: "", phone: "", note: "", resume: null };

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobDetail, setSelectedJobDetail] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [resumeError, setResumeError] = useState("");
  const [resumeFileName, setResumeFileName] = useState("");
  const jobDetailBodyRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    document.title = 'Careers | Softblitz Resources Inc';
    return () => {
      document.title = 'Softblitz Resources Inc';
    };
  }, []);
  // --- Fetch jobs from API ---
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/careers`);
        if (!res.ok) throw new Error("Failed to fetch jobs");
        const data = await res.json();
        setJobs(data.jobs || []);
      } catch (err) {
        console.error("Failed to fetch jobs:", err);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  useEffect(() => {
    if (selectedJobDetail || selectedJob) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [selectedJobDetail, selectedJob]);

  useEffect(() => {
    if (selectedJobDetail) {
      setTimeout(() => {
        const el = jobDetailBodyRef.current;
        if (el && el.scrollHeight > el.clientHeight) {
          setShowScrollHint(true);
        } else {
          setShowScrollHint(false);
        }
      }, 100);
    }
  }, [selectedJobDetail]);

  const handleDetailScroll = () => {
    const el = jobDetailBodyRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 10;
    setShowScrollHint(!atBottom);
  };

  const validatePhone = (phone) => {
    const phoneRegex =
      /^\+?[1-9]\d{0,2}[\s\-.]?\(?\d{3}\)?[\s\-.]?\d{3}[\s\-.]?\d{4}$/;
    return phoneRegex.test(phone.trim());
  };

  const handleFormChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "resume" && files) {
      if (files.length === 0) return;

      const file = files[0];
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      const maxSize = 5 * 1024 * 1024;

      if (!allowedTypes.includes(file.type)) {
        setResumeError("Only PDF, DOC, or DOCX files are allowed.");
        setResumeFileName("");
        setForm((prev) => ({ ...prev, resume: null }));
        return;
      }
      if (file.size > maxSize) {
        setResumeError("File size must be under 5MB.");
        setResumeFileName("");
        setForm((prev) => ({ ...prev, resume: null }));
        return;
      }
      setResumeError("");
      setResumeFileName(file.name);
      setForm((prev) => ({ ...prev, resume: file }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "phone") {
      if (!value.trim()) {
        setPhoneError("Phone number is required.");
      } else if (!validatePhone(value)) {
        setPhoneError("Enter a valid phone number e.g. +1 (555) 000-0000");
      } else {
        setPhoneError("");
      }
    }
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();

    if (!form.phone.trim()) {
      setPhoneError("Phone number is required.");
      return;
    }
    if (!validatePhone(form.phone)) {
      setPhoneError("Enter a valid phone number e.g. +1 (555) 000-0000");
      return;
    }
    if (!form.resume) {
      setResumeError("Please upload your resume.");
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const formData = new FormData();
      formData.append("jobId", selectedJob.jobId);
      formData.append("jobTitle", selectedJob.title);
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);
      formData.append("note", form.note);
      formData.append("resume", form.resume);

      const res = await fetch(`${API_BASE_URL}/careers/apply`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Submission failed");

      setSubmitStatus("success");
      setForm(EMPTY_FORM);
    } catch (err) {
      console.error("Submission error:", err);
      setSubmitStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setSelectedJob(null);
    setSelectedJobDetail(null);
    setSubmitStatus(null);
    setForm(EMPTY_FORM);
    setPhoneError("");
    setResumeError("");
    setResumeFileName("");
    setShowScrollHint(false);
  };

  return (
    <div className="careers-page">
      <Header />

      {/* Hero */}
      <section className="careers-hero">
        <div className="careers-hero-text">
          <h1>
            Build the Future <span>With Us</span>
          </h1>
          <p>
            At Softblitz Resources Inc, we're always looking for passionate
            technologists to help our clients navigate digital transformation.
            Explore open roles below.
          </p>
        </div>
        <div className="careers-hero-stats">
          <div className="stat">
            <span>{jobs.length}</span>
            <p>Open Roles</p>
          </div>
          <div className="stat">
            <span>100%</span>
            <p>Remote Friendly</p>
          </div>
          <div className="stat">
            <span>∞</span>
            <p>Growth Opportunities</p>
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="careers-listings">
        <h2>Current Openings</h2>

        {loading ? (
          <div className="careers-loading">
            <div className="spinner" />
            <p>Loading openings...</p>
          </div>
        ) : jobs.length === 0 ? (
          <p className="careers-empty">
            No openings at the moment. Check back soon!
          </p>
        ) : (
          <div className="jobs-list">
            {jobs.map((job) => (
              <div
                className="job-row"
                key={job.jobId}
                onClick={() => setSelectedJobDetail(job)}
              >
                <div className="job-row-left">
                  <h3>{job.title}</h3>
                  <div className="job-tags">
                    <span className="tag">{job.department}</span>
                    <span className="tag">{job.type}</span>
                    <span className="tag">
                      <FaMapMarkerAlt
                        style={{ marginRight: "4px", verticalAlign: "middle" }}
                      />
                      {job.location}
                    </span>
                  </div>
                  <p className="job-description-preview">{job.description}</p>
                  <span className="view-details-link">View details →</span>
                </div>
                <div
                  className="job-row-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="apply-btn"
                    onClick={() => {
                      setSelectedJob(job);
                      setSubmitStatus(null);
                    }}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Why Join Us */}
      <section className="careers-why">
        <h2>Why Softblitz?</h2>
        <div className="why-grid">
          {[
            {
              icon: <FaGlobe />,
              title: "Remote-First",
              text: "Work from anywhere with a flexible, async-friendly culture.",
            },
            {
              icon: <FaChartLine />,
              title: "Career Growth",
              text: "Clear paths for advancement with mentorship and learning support.",
            },
            {
              icon: <FaHandshake />,
              title: "Collaborative Team",
              text: "Work alongside experienced engineers, consultants, and leaders.",
            },
            {
              icon: <FaLightbulb />,
              title: "Impactful Work",
              text: "Solve real problems for clients across industries and sectors.",
            },
          ].map((item, i) => (
            <div className="why-card" key={i}>
              <div className="why-icon">{item.icon}</div>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer />

      {/* Job Detail Modal */}
      {selectedJobDetail && (
        <div
          className="modal-overlay open"
          onClick={() => setSelectedJobDetail(null)}
        >
          <div
            className="modal-content job-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-button"
              onClick={() => setSelectedJobDetail(null)}
            >
              ×
            </button>

            <div className="job-detail-header">
              <h2>{selectedJobDetail.title}</h2>
              <div className="job-tags" style={{ marginTop: "0.5rem" }}>
                <span className="tag">{selectedJobDetail.department}</span>
                <span className="tag">{selectedJobDetail.type}</span>
                <span className="tag">
                  <FaMapMarkerAlt
                    style={{ marginRight: "4px", verticalAlign: "middle" }}
                  />
                  {selectedJobDetail.location}
                </span>
              </div>
            </div>

            <div className="job-detail-body-wrapper">
              <div
                className="job-detail-body"
                ref={jobDetailBodyRef}
                onScroll={handleDetailScroll}
              >
                <h4>About the Role</h4>
                <p>{selectedJobDetail.description}</p>
              </div>

              {showScrollHint && (
                <>
                  <div className="scroll-hint-fade" />
                  <div
                    className="scroll-hint-btn"
                    onClick={() => {
                      jobDetailBodyRef.current?.scrollBy({
                        top: 120,
                        behavior: "smooth",
                      });
                    }}
                  >
                    <FaChevronDown />
                  </div>
                </>
              )}
            </div>

            <div className="job-detail-footer">
              <button
                className="apply-btn submit-btn"
                onClick={() => {
                  setSelectedJobDetail(null);
                  setSelectedJob(selectedJobDetail);
                  setSubmitStatus(null);
                }}
              >
                Apply Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {selectedJob && (
        <div className="modal-overlay open" onClick={closeModal}>
          <div
            className="modal-content careers-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-button" onClick={closeModal}>
              ×
            </button>
            <h2>
              Apply for <span>{selectedJob.title}</span>
            </h2>
            <p className="modal-job-meta">
              {selectedJob.type} · {selectedJob.location}
            </p>

            {submitStatus === "success" ? (
              <div className="submit-success">
                <div className="success-icon">✅</div>
                <h3>Application Submitted!</h3>
                <p>
                  Thanks for applying. We'll be in touch at the email you
                  provided.
                </p>
                <button className="apply-btn" onClick={closeModal}>
                  Close
                </button>
              </div>
            ) : (
              <form className="apply-form" onSubmit={handleApplySubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      name="name"
                      type="text"
                      placeholder="Jane Doe"
                      value={form.name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email Address *</label>
                    <input
                      name="email"
                      type="email"
                      placeholder="jane@email.com"
                      value={form.email}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    name="phone"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={form.phone}
                    onChange={handleFormChange}
                    style={{ borderColor: phoneError ? "#e53e3e" : "" }}
                  />
                  {phoneError && (
                    <span className="field-error">{phoneError}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Cover Note</label>
                  <textarea
                    name="note"
                    rows="4"
                    placeholder="Tell us why you're a great fit..."
                    value={form.note}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="form-group">
                  <label>Resume / CV *</label>
                  <div
                    className={`file-upload-wrapper ${resumeError ? "file-error" : ""} ${resumeFileName ? "file-selected" : ""}`}
                  >
                    <input
                      name="resume"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFormChange}
                      className="file-input-hidden"
                      id="resume-upload"
                    />
                    <label
                      htmlFor="resume-upload"
                      className="file-upload-label"
                    >
                      {resumeFileName ? (
                        <>
                          <span className="file-icon">📄</span>
                          <span className="file-name">{resumeFileName}</span>
                          <span className="file-change">Change</span>
                        </>
                      ) : (
                        <>
                          <span className="file-icon">⬆️</span>
                          <span className="file-placeholder">
                            Click to upload PDF, DOC, or DOCX
                          </span>
                          <span className="file-hint-inline">Max 5MB</span>
                        </>
                      )}
                    </label>
                  </div>
                  {resumeError && (
                    <span className="field-error">{resumeError}</span>
                  )}
                </div>
                {submitStatus === "error" && (
                  <p className="submit-error">
                    Something went wrong. Please try again.
                  </p>
                )}
                <button
                  type="submit"
                  className="apply-btn submit-btn"
                  disabled={submitting}
                >
                  {submitting ? (
                    <span className="btn-spinner-wrapper">
                      <span className="btn-spinner" /> Submitting...
                    </span>
                  ) : (
                    "Submit Application"
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Careers;
